import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Crud, CrudFormComponent, CrudMode, CrudProps, notification, rejectionError, transformCrudFormErrors, ValidationError } from '@ohpen/enso-ui';

import { RegionResponse, regionCreate, regionUpdate } from '@pmp/api';
import { CrudFormProps } from '@pmp/types';

import { getApiErrorMessage } from '@pmp/utils';

import { getRegionSchema } from '../regionsBdmsFormConfig';
import { toCrudFieldErrors } from '../utils';

type RegionFormValues = { name: string };

export const RegionCrudForm = ({ mode, record, onClose, refreshTable }: CrudFormProps<RegionResponse>): ReactElement => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  const onConfirm = useCallback(
    async (values: RegionFormValues): Promise<ValidationError[] | void> => {
      try {
        if (mode === CrudMode.EDIT && record) {
          await regionUpdate({ path: { id: record.id }, body: { name: values.name } });
          notification().success({ title: t('lender.regionsBdms.labels.regionUpdated', { name: values.name }) });
        } else {
          await regionCreate({ body: { name: values.name } });
          notification().success({ title: t('lender.regionsBdms.labels.regionCreated', { name: values.name }) });
        }
        refreshTable();
      } catch (error) {
        const fieldErrors = toCrudFieldErrors(error);
        if (fieldErrors) return transformCrudFormErrors(fieldErrors);
        return Promise.reject(rejectionError(getApiErrorMessage(error)));
      }
    },
    [mode, record, refreshTable, t],
  );

  const crudProps: CrudProps<RegionFormValues> = useMemo(
    () => ({
      ref,
      open,
      schema: getRegionSchema(t),
      title: mode === CrudMode.EDIT ? t('lender.regionsBdms.labels.editRegion') : t('lender.regionsBdms.labels.createRegion'),
      labels: { confirm: t('global.buttons.save') },
      initialValues: record ? { name: record.name } : null,
      mode,
      onClose: handleOnClose,
      onConfirm,
    }),
    [open, mode, record, t, handleOnClose, onConfirm],
  );

  return <Crud {...crudProps} />;
};
